import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, FileText, Receipt, Ticket, Users } from "lucide-react";
import AppLayout from "@/components/AppLayout";
import { StatusBadge } from "@/components/StatusBadge";
import { ErrorState, LoadingState, PermissionDeniedState } from "@/components/States";
import BookingsTab from "@/components/trip/BookingsTab";
import DocumentsTab from "@/components/trip/DocumentsTab";
import FinanceTab from "@/components/trip/FinanceTab";
import ParticipantsTab from "@/components/trip/ParticipantsTab";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError, isPermissionError } from "@/lib/api";

const SECTIONS = {
  participants: {
    icon: Users,
    title: "Participants",
    subtitle: "Invite travellers, track joins, and manage the trip roster.",
    loading: "Loading participants workspace...",
  },
  bookings: {
    icon: Ticket,
    title: "Bookings",
    subtitle: "Create bookings, review capacity, and follow booking changes.",
    loading: "Loading bookings workspace...",
  },
  finance: {
    icon: Receipt,
    title: "Budget and payments",
    subtitle: "Review ledger totals, outstanding balances, and trip expenses.",
    loading: "Loading budget workspace...",
  },
  documents: {
    icon: FileText,
    title: "Documents",
    subtitle: "Manage permits, waivers, insurance, and operational document links.",
    loading: "Loading documents workspace...",
  },
};

export default function TripOperationsPage({ section: fixedSection }) {
  const { id, section: routeSection } = useParams();
  const section = fixedSection || routeSection;
  const { perms, roles } = useAuth();
  const [trip, setTrip] = useState(null);
  const [error, setError] = useState("");
  const [denied, setDenied] = useState(false);

  const meta = SECTIONS[section] || SECTIONS.participants;
  const Icon = meta.icon;

  const load = useCallback(async () => {
    setError("");
    setDenied(false);
    try {
      const { data } = await api.get(`/trips/${id}`);
      setTrip(data);
    } catch (err) {
      const message = formatApiError(err, `Could not load ${meta.title.toLowerCase()}.`);
      setError(message);
      setDenied(isPermissionError(err));
    }
  }, [id, meta.title]);

  useEffect(() => { load(); }, [load]);

  const allowed = useMemo(() => {
    if (!trip) return false;
    if (section === "participants") return perms.includes("participant:view");
    if (section === "bookings") return perms.includes("booking:manage") || perms.includes("finance:reconcile");
    if (section === "finance") return perms.includes("finance:reconcile");
    if (section === "documents") return trip.can_edit || perms.includes("doc:review") || roles.includes("guide");
    return false;
  }, [perms, roles, section, trip]);

  if (denied) return <AppLayout><PermissionDeniedState message={error} /></AppLayout>;
  if (error) return <AppLayout><ErrorState message={error} onRetry={load} /></AppLayout>;
  if (!trip) return <AppLayout><LoadingState label={meta.loading} /></AppLayout>;
  if (!allowed) return <AppLayout><PermissionDeniedState message={`You do not have permission to view ${meta.title.toLowerCase()}.`} /></AppLayout>;

  return (
    <AppLayout>
      <div className="tp-page-wrap space-y-5">
        <Link to={`/trips/${id}`} className="inline-flex min-h-10 items-center gap-2 rounded-xl text-sm font-bold text-[#516259] hover:text-[#2D7A65] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E5B85C] focus-visible:ring-offset-2">
          <ArrowLeft className="h-4 w-4" /> Back to run overview
        </Link>

        <section className="tp-paper rounded-[2rem] p-5 sm:p-6">
          <p className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.18em] text-[#2D7A65]">
            <Icon className="h-4 w-4" /> Trip operations
          </p>
          <div className="mt-3 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="font-[Plus_Jakarta_Sans,sans-serif] text-3xl font-black text-[#17211D] sm:text-4xl">{meta.title}</h1>
                <StatusBadge state={trip.state} />
              </div>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">{meta.subtitle}</p>
              <p className="mt-1 text-xs font-bold text-[#516259]">{trip.title}</p>
            </div>
          </div>
        </section>

        <section className="tp-paper rounded-[1.75rem] p-4 sm:p-5">
          {section === "participants" && <ParticipantsTab trip={trip} canEdit={trip.can_edit} />}
          {section === "bookings" && <BookingsTab trip={trip} canEdit={trip.can_edit} />}
          {section === "finance" && <FinanceTab trip={trip} canEdit={trip.can_edit} />}
          {section === "documents" && <DocumentsTab trip={trip} canEdit={trip.can_edit} />}
        </section>
      </div>
    </AppLayout>
  );
}
